import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Input, Row, Col, Icon, Tag, Radio, Alert, Button, Tooltip, Spin, Upload, message } from 'antd';
import BraftEditor from 'braft-editor';
import 'braft-editor/dist/index.css';

import { listarTemplates, previewRascunho, enviarArquivo, removerArquivo } from '../api';

const CONTROLES = [
  'bold', 'italic', 'underline', 'text-color', 'separator',
  'link', 'separator', 'list-ul', 'list-ol', 'separator', 'remove-styles',
];

/**
 * O editor guarda o próprio EditorState.
 *
 * Passar `BraftEditor.createEditorState(html)` direto na prop `value` cria um
 * objeto novo a cada render: o editor entende como conteúdo novo, dispara
 * onChange, o pai re-renderiza e o ciclo não fecha — "Maximum update depth
 * exceeded" e a tela em branco assim que se digita em qualquer outro campo.
 * Com o estado aqui dentro a identidade é estável; para reaproveitar o
 * componente com outro conteúdo, use `key` no pai.
 */
const EditorRico = ({ valorInicial, onChange, altura }) => {
  const [estado, setEstado] = useState(function () {
    return BraftEditor.createEditorState(valorInicial || '');
  });

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
      <BraftEditor
        language="pt-br"
        controls={CONTROLES}
        contentStyle={{ height: altura || 180 }}
        value={estado}
        onChange={(novo) => {
          setEstado(novo);
          onChange(novo.toHTML());
        }}
      />
    </div>
  );
};

/**
 * Miniatura de um modelo: o próprio e-mail, montado no servidor com conteúdo
 * de exemplo e reduzido por escala.
 *
 * Nada de imagem estática — captura de tela envelhece no dia em que alguém
 * mexe no template, e ninguém percebe.
 */
const Miniatura = ({ html, altura }) => (
  <div
    style={{
      height: altura, overflow: 'hidden', position: 'relative',
      background: '#f0f2f5', borderBottom: '1px solid #e8e8e8',
    }}
  >
    <iframe
      title="modelo"
      srcDoc={html}
      sandbox=""
      scrolling="no"
      style={{
        width: 640, height: Math.round(altura / 0.33), border: 0,
        transform: 'scale(0.33)', transformOrigin: 'top left',
        position: 'absolute', top: 0, left: 0, pointerEvents: 'none',
      }}
    />
  </div>
);

const CartaoModelo = ({ template, escolhido, onEscolher }) => (
  <div
    onClick={onEscolher}
    data-modelo={template.slug}
    style={{
      border: '1px solid ' + (escolhido ? '#1890ff' : '#e8e8e8'),
      boxShadow: escolhido ? '0 0 0 3px rgba(24,144,255,.14)' : '0 1px 3px rgba(15,20,30,.06)',
      borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: '#fff',
      transition: 'box-shadow .15s, border-color .15s',
    }}
  >
    <Miniatura html={template.preview_html} altura={186} />
    <div style={{ padding: '10px 12px 12px' }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2430' }}>
        {escolhido && <Icon type="check-circle" theme="filled" style={{ color: '#1890ff', marginRight: 6 }} />}
        {template.name}
      </div>
      <div style={{ fontSize: 12, color: '#8c8c8c', lineHeight: 1.45, marginTop: 3 }}>
        {template.description}
      </div>
    </div>
  </div>
);

/** Editor dos blocos do Boletim — o campo `blocks`, que antes caía num input solto. */
const EditorBlocos = ({ valor, max, onChange }) => {
  const blocos = Array.isArray(valor) ? valor : [];
  const limite = max || 4;

  const trocar = (i, chave, v) =>
    onChange(blocos.map((b, j) => (j === i ? { ...b, [chave]: v } : b)));

  return (
    <div>
      {blocos.map((bloco, i) => (
        <div
          key={i}
          style={{
            border: '1px solid #e8e8e8', borderRadius: 8, padding: 12,
            marginBottom: 10, background: '#fafbfc',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{
              width: 20, height: 20, borderRadius: '50%', background: '#1890ff', color: '#fff',
              fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', marginRight: 8, flex: '0 0 auto',
            }}>{i + 1}</span>
            <Input
              size="small"
              placeholder="Título do assunto"
              value={bloco.titulo || ''}
              onChange={(e) => trocar(i, 'titulo', e.target.value)}
            />
            <Tooltip title="Remover assunto">
              <Button
                size="small"
                type="link"
                icon="delete"
                style={{ color: '#bfbfbf', marginLeft: 4 }}
                onClick={() => onChange(blocos.filter((_, j) => j !== i))}
              />
            </Tooltip>
          </div>

          <EditorRico
            key={'bloco-' + i}
            altura={110}
            valorInicial={bloco.texto}
            onChange={(html) => trocar(i, 'texto', html)}
          />

          <Input
            size="small"
            style={{ marginTop: 8 }}
            addonBefore={<Icon type="link" />}
            placeholder="Link do assunto (opcional)"
            value={bloco.link || ''}
            onChange={(e) => trocar(i, 'link', e.target.value)}
          />
        </div>
      ))}

      {blocos.length < limite ? (
        <Button
          icon="plus"
          style={{ width: '100%', borderStyle: 'dashed', borderRadius: 8 }}
          onClick={() => onChange(blocos.concat([{ titulo: '', texto: '', link: '' }]))}
        >
          Acrescentar assunto
        </Button>
      ) : (
        <div style={{ fontSize: 12, color: '#8c8c8c', textAlign: 'center' }}>
          Máximo de {limite} assuntos — boletim longo ninguém lê até o fim.
        </div>
      )}
    </div>
  );
};

/**
 * Passo 2 — o que vai ser enviado.
 *
 * A tela é dividida: campos de um lado, e-mail montado do outro, atualizando
 * enquanto se digita. Sem isso o autor escreve às cegas e só descobre como
 * ficou no passo seguinte, quando já não está pensando no texto.
 */
const PassoConteudo = ({ campanha, onChange }) => {
  const [templates, setTemplates] = useState([]);
  const [variaveis, setVariaveis] = useState({});
  const [modo, setModo] = useState(campanha.body_html && !campanha.template_slug ? 'html' : 'template');
  const [trocandoModelo, setTrocandoModelo] = useState(!campanha.template_slug);
  const [previa, setPrevia] = useState({ html: '', amostra: null, remetente: '', carregando: false });
  const [dispositivo, setDispositivo] = useState('desktop');
  const [subindo, setSubindo] = useState(null);
  const debounce = useRef(null);
  // A prévia é assíncrona e o usuário avança de passo sem esperar por ela;
  // sem esta guarda o `then` cai num componente já desmontado.
  const montado = useRef(true);
  useEffect(() => () => { montado.current = false; }, []);

  useEffect(() => {
    listarTemplates().then((dados) => {
      if (!dados) return;
      setTemplates(dados.templates || []);
      setVariaveis(dados.variaveis || {});
    });
  }, []);

  const template = templates.filter((t) => t.slug === campanha.template_slug)[0];
  const conteudo = campanha.content || {};

  // A prévia é montada pelo servidor, pela mesma função que monta o e-mail no
  // envio — repetir os cinco layouts em JS aqui garantiria divergência.
  const atualizarPrevia = useCallback((dados) => {
    setPrevia((p) => ({ ...p, carregando: true }));
    previewRascunho(dados)
      .then((r) => {
        if (!montado.current || !r) return;
        setPrevia({ html: r.html || '', amostra: r.amostra, remetente: r.remetente, carregando: false });
      })
      .catch(() => montado.current && setPrevia((p) => ({ ...p, carregando: false })));
  }, []);

  useEffect(() => {
    if (!campanha.template_slug && !campanha.body_html) {
      setPrevia((p) => ({ ...p, html: '', amostra: null, carregando: false }));
      return;
    }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => atualizarPrevia({
      template_slug: campanha.template_slug,
      content: campanha.content,
      body_html: modo === 'html' ? campanha.body_html : null,
      subject: campanha.subject,
      preheader: campanha.preheader,
    }), 650);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [campanha.template_slug, campanha.content, campanha.body_html, campanha.subject, campanha.preheader, modo, atualizarPrevia]);

  const setCampo = (chave, valor) =>
    onChange({ ...campanha, content: { ...conteudo, [chave]: valor } });

  const escolherModelo = (slug) => {
    onChange({ ...campanha, template_slug: slug, body_html: null });
    setTrocandoModelo(false);
  };

  const renderCampo = (campo) => {
    const valor = conteudo[campo.key];

    if (campo.type === 'richtext') {
      return (
        <EditorRico
          key={(campanha.template_slug || '') + ':' + campo.key}
          valorInicial={valor}
          onChange={(html) => setCampo(campo.key, html)}
        />
      );
    }

    if (campo.type === 'blocks') {
      return (
        <EditorBlocos
          valor={valor}
          max={campo.max}
          onChange={(blocos) => setCampo(campo.key, blocos)}
        />
      );
    }

    if (campo.type === 'image') {
      const url = typeof valor === 'string' ? valor : (valor && valor.url) || '';
      return (
        <div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(arquivo) => {
                setSubindo(campo.key);
                enviarArquivo(arquivo, 'imagem')
                  .then((r) => r && setCampo(campo.key, r.url))
                  .catch((e) => message.error(
                    (e && e.response && e.response.data && e.response.data.message) ||
                    'Não foi possível enviar a imagem.'
                  ))
                  .finally(() => setSubindo(null));
                return false; // o envio é nosso, não do componente
              }}
            >
              <Button icon="upload" loading={subindo === campo.key}>Enviar imagem</Button>
            </Upload>

            <Input
              placeholder="ou cole o endereço de uma imagem já publicada"
              value={url}
              onChange={(e) => setCampo(campo.key, e.target.value)}
            />
          </div>

          {url ? (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <img
                src={url}
                alt=""
                style={{ maxWidth: 240, maxHeight: 110, borderRadius: 6, display: 'block', border: '1px solid #e8e8e8' }}
              />
              <Button size="small" icon="delete" onClick={() => setCampo(campo.key, '')}>Tirar</Button>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 5 }}>
              Até 10 MB. A imagem fica publicada num endereço próprio — cliente de
              e-mail não abre arquivo do seu computador.
            </div>
          )}
        </div>
      );
    }

    if (campo.type === 'button') {
      const botao = valor || {};
      return (
        <Row gutter={8}>
          <Col span={10}>
            <Input
              placeholder="Texto do botão"
              value={botao.label || ''}
              onChange={(e) => setCampo(campo.key, { ...botao, label: e.target.value })}
            />
          </Col>
          <Col span={14}>
            <Input
              placeholder="https://..."
              value={botao.url || ''}
              onChange={(e) => setCampo(campo.key, { ...botao, url: e.target.value })}
            />
          </Col>
        </Row>
      );
    }

    return (
      <Input
        placeholder={campo.placeholder || ''}
        value={valor || ''}
        onChange={(e) => setCampo(campo.key, e.target.value)}
      />
    );
  };

  const rotulo = (campo) => (
    <div style={{ fontSize: 12, color: '#595959', marginBottom: 5, fontWeight: 500 }}>
      {campo.label}
      {campo.required && <span style={{ color: '#f5222d' }}> *</span>}
    </div>
  );

  const tamanhoAssunto = (campanha.subject || '').length;

  const cabecalho = (
    <Card size="small" style={{ marginBottom: 14, borderRadius: 8 }}>
      <div style={{ fontSize: 12, color: '#595959', marginBottom: 5, fontWeight: 500 }}>
        Assunto do e-mail
      </div>
      <Input
        size="large"
        placeholder="O que a pessoa vê antes de abrir"
        value={campanha.subject || ''}
        onChange={(e) => onChange({ ...campanha, subject: e.target.value })}
      />
      <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 5 }}>
        {tamanhoAssunto > 60 ? (
          <span style={{ color: '#fa8c16' }}>
            <Icon type="warning" /> {tamanhoAssunto} caracteres — a caixa de entrada do celular corta perto de 40.
          </span>
        ) : (
          tamanhoAssunto + ' caracteres · o celular mostra cerca de 40'
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, color: '#595959', marginBottom: 5, fontWeight: 500 }}>
          Pré-cabeçalho
          <Tooltip title="A segunda linha que a caixa de entrada mostra ao lado do assunto. Sem ela, o cliente de e-mail preenche sozinho com o começo do HTML.">
            <Icon type="question-circle" style={{ color: '#bfbfbf', marginLeft: 5 }} />
          </Tooltip>
        </div>
        <Input
          placeholder="Ex.: veja o que muda na sua apuração a partir de janeiro"
          value={campanha.preheader || ''}
          onChange={(e) => onChange({ ...campanha, preheader: e.target.value })}
        />
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, color: '#595959', marginBottom: 5, fontWeight: 500 }}>
          Nome que aparece como remetente
        </div>
        <Input
          placeholder="Nome do escritório"
          value={campanha.from_name || ''}
          onChange={(e) => onChange({ ...campanha, from_name: e.target.value })}
          style={{ maxWidth: 420 }}
        />
        {/* O endereço não é escolha do usuário: SPF e DKIM são assinados pelo
            domínio do sistema, e um `from` de outro domínio não alinha — o
            DMARC do destinatário manda a campanha para o lixo eletrônico. */}
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
          <Icon type="safety-certificate" style={{ marginRight: 4 }} />
          O e-mail sai de <strong>{previa.remetente || 'endereço do sistema'}</strong>, que é o
          domínio autenticado. Endereço próprio faria a mensagem cair em spam.
        </div>
      </div>
    </Card>
  );

  const seletorDeModo = (
    <Radio.Group value={modo} onChange={(e) => setModo(e.target.value)} size="small">
      <Radio.Button value="template">Usar um modelo</Radio.Button>
      <Radio.Button value="html">Colar um HTML pronto</Radio.Button>
    </Radio.Group>
  );

  // ── galeria: ocupa a tela inteira enquanto não há modelo escolhido ───────
  if (modo === 'template' && (trocandoModelo || !template)) {
    return (
      <div>
        {cabecalho}

        <Card
          size="small"
          style={{ borderRadius: 8 }}
          title={<span style={{ fontSize: 14 }}>Escolha o formato do e-mail</span>}
          extra={
            <span>
              {template && (
                <Button size="small" style={{ marginRight: 8 }} onClick={() => setTrocandoModelo(false)}>
                  Cancelar
                </Button>
              )}
              {seletorDeModo}
            </span>
          }
        >
          <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 14 }}>
            Cada cartão é o e-mail de verdade, montado com um conteúdo de exemplo.
            O layout é fechado de propósito: é o que não quebra no Outlook.
          </div>

          {templates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(196px, 1fr))',
              gap: 14,
            }}>
              {templates.map((t) => (
                <CartaoModelo
                  key={t.slug}
                  template={t}
                  escolhido={t.slug === campanha.template_slug}
                  onEscolher={() => escolherModelo(t.slug)}
                />
              ))}
            </div>
          )}
        </Card>
      </div>
    );
  }

  // ── edição: campos à esquerda, e-mail montado à direita ──────────────────
  const painelPrevia = (
    <Card
      size="small"
      style={{ borderRadius: 8, position: 'sticky', top: 12 }}
      bodyStyle={{ padding: 0, background: '#f0f2f5' }}
      title={
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          Como vai chegar
          {previa.carregando && <Spin size="small" style={{ marginLeft: 8 }} />}
        </span>
      }
      extra={
        <Radio.Group size="small" value={dispositivo} onChange={(e) => setDispositivo(e.target.value)}>
          <Radio.Button value="desktop"><Icon type="desktop" /></Radio.Button>
          <Radio.Button value="celular"><Icon type="mobile" /></Radio.Button>
        </Radio.Group>
      }
    >
      <div style={{ padding: dispositivo === 'celular' ? '14px 0' : 0, display: 'flex', justifyContent: 'center' }}>
        <iframe
          title="Prévia do e-mail"
          srcDoc={previa.html || '<div style="font:14px sans-serif;color:#8c8c8c;padding:44px;text-align:center">Preencha o conteúdo para ver a prévia.</div>'}
          sandbox=""
          style={{
            width: dispositivo === 'celular' ? 380 : '100%',
            height: 620,
            border: dispositivo === 'celular' ? '1px solid #d9d9d9' : 0,
            borderRadius: dispositivo === 'celular' ? 10 : 0,
            display: 'block', background: '#f0f2f5',
          }}
        />
      </div>
      <div style={{ padding: '8px 12px', fontSize: 11, color: '#8c8c8c', background: '#fff', borderTop: '1px solid #e8e8e8' }}>
        <Icon type="lock" /> Roda isolada — o HTML não executa script.
        {previa.amostra && ' Variáveis preenchidas com os dados de ' + previa.amostra + '.'}
      </div>
    </Card>
  );

  return (
    <div>
      {cabecalho}

      <Row gutter={16}>
        <Col xs={24} lg={13}>
          <Card
            size="small"
            style={{ marginBottom: 14, borderRadius: 8 }}
            title={
              modo === 'template' && template ? (
                <span style={{ fontSize: 13, fontWeight: 500 }}>
                  <Icon type="layout" style={{ color: '#1890ff', marginRight: 6 }} />
                  {template.name}
                </span>
              ) : (
                <span style={{ fontSize: 13, fontWeight: 500 }}>HTML importado</span>
              )
            }
            extra={
              <span>
                {modo === 'template' && (
                  <Button size="small" style={{ marginRight: 8 }} onClick={() => setTrocandoModelo(true)}>
                    Trocar modelo
                  </Button>
                )}
                {seletorDeModo}
              </span>
            }
          >
            {modo === 'template' ? (
              <div>
                {(template.fields || []).map((campo) => (
                  <div key={campo.key} style={{ marginBottom: 16 }}>
                    {rotulo(campo)}
                    {renderCampo(campo)}
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="O HTML é enviado como está"
                  description="Só o cabeçalho, o rodapé e o link de descadastro são acrescentados. Verifique no e-mail de teste antes de disparar."
                />
                <Input.TextArea
                  rows={16}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                  placeholder="<html>…"
                  value={campanha.body_html || ''}
                  onChange={(e) => onChange({ ...campanha, body_html: e.target.value, template_slug: null })}
                />
              </div>
            )}
          </Card>

          <Card
            size="small"
            style={{ borderRadius: 8, marginBottom: 14 }}
            title={<span style={{ fontSize: 13, fontWeight: 500 }}>Anexos</span>}
            extra={
              <Upload
                showUploadList={false}
                beforeUpload={(arquivo) => {
                  setSubindo('anexo');
                  enviarArquivo(arquivo, 'anexo')
                    .then((r) => {
                      if (!r) return;
                      setCampo('anexos', (conteudo.anexos || []).concat([r]));
                    })
                    .catch((e) => message.error(
                      (e && e.response && e.response.data && e.response.data.message) ||
                      'Não foi possível enviar o anexo.'
                    ))
                    .finally(() => setSubindo(null));
                  return false;
                }}
              >
                <Button size="small" icon="paper-clip" loading={subindo === 'anexo'}>Anexar</Button>
              </Upload>
            }
          >
            {(conteudo.anexos || []).length === 0 ? (
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                Nenhum anexo. Cada arquivo vai junto em todos os e-mails da campanha —
                acima de uns 5 MB no total, provedor começa a recusar.
              </div>
            ) : (
              (conteudo.anexos || []).map((a, i) => (
                <div
                  key={a.caminho || i}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                    padding: '6px 0', borderBottom: '1px solid #f5f5f5',
                  }}
                >
                  <Icon type="paper-clip" style={{ color: '#8c8c8c' }} />
                  <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.nome}
                  </a>
                  <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                    {Math.max(1, Math.round((a.tamanho || 0) / 1024))} KB
                  </span>
                  <Button
                    size="small" type="link" icon="delete" style={{ color: '#bfbfbf' }}
                    onClick={() => {
                      setCampo('anexos', (conteudo.anexos || []).filter((x, j) => j !== i));
                      if (a.caminho) removerArquivo(a.caminho).catch(() => {});
                    }}
                  />
                </div>
              ))
            )}
          </Card>

          <Card
            size="small"
            style={{ borderRadius: 8 }}
            title={<span style={{ fontSize: 13, fontWeight: 500 }}>Variáveis</span>}
          >
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
              Escreva no texto e o sistema troca pelo dado de cada destinatário.
            </div>
            {Object.keys(variaveis).map((chave) => (
              <Tooltip key={chave} title={variaveis[chave]}>
                <Tag style={{ marginBottom: 6, fontFamily: 'monospace', cursor: 'default' }}>
                  {'{{' + chave + '}}'}
                </Tag>
              </Tooltip>
            ))}
          </Card>
        </Col>

        <Col xs={24} lg={11}>
          {painelPrevia}
        </Col>
      </Row>
    </div>
  );
};

export default PassoConteudo;
